<script setup>
import { ref, computed } from "vue";
import { useLanguageStore } from "@/pinia";

const languageStore = useLanguageStore();

const props = defineProps({
  isDeleteModal: { default: false },
  selectedVideos: { default: [] }
});

const emits = defineEmits(['close', 'confirmDelete']);
const deleteSnsVideo = ref(false);

// SNS 연동 영상 존재 여부
const hasLinkedSns = computed(() => {
  return props.selectedVideos.some(v => v.snsVideo && v.snsVideo.length > 0);
});

// SNS 연동된 영상 목록
const linkedSnsVideos = computed(() => {
  return props.selectedVideos.filter(v => v.snsVideo && v.snsVideo.length > 0);
});

// SNS 타입별 개수
const snsTypeCounts = computed(() => {
  const counts = {};
  linkedSnsVideos.value.forEach(video => {
    video.snsVideo.forEach(sns => {
      counts[sns.snsType] = (counts[sns.snsType] || 0) + 1;
    });
  });
  return counts;
});

const close = () => {
  deleteSnsVideo.value = false;
  emits('close');
};

const confirmDelete = () => {
  emits('confirmDelete', deleteSnsVideo.value);
  close();
};
</script>

<template>
  <v-dialog
      :model-value="isDeleteModal"
      @update:modelValue="close"
      width="500"
      persistent
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('deleteConfirm') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <p class="mb-4">{{ languageStore.get('deleteVideoConfirmMessage') }}</p>

        <template v-if="hasLinkedSns">
          <v-alert type="info" variant="tonal" class="mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon class="mr-2">mdi-alert-circle-outline</v-icon>
              <span>{{ languageStore.get('snsUploadedVideoCount').replace('{count}', linkedSnsVideos.length) }}</span>
            </div>
            <div class="sns-info">
              <template v-for="(count, type) in snsTypeCounts" :key="type">
                <v-chip size="small" class="mr-2" v-if="type === 'youtube'" color="red">
                  <v-icon size="16" class="mr-1">mdi-youtube</v-icon>
                  YouTube: {{ count }}{{ languageStore.get('countUnit') }}
                </v-chip>
                <v-chip size="small" class="mr-2" v-else-if="type === 'meta'" color="blue">
                  <v-icon size="16" class="mr-1">mdi-facebook</v-icon>
                  Facebook: {{ count }}{{ languageStore.get('countUnit') }}
                </v-chip>
                <v-chip size="small" class="mr-2" v-else-if="type === 'twitter'" color="light-blue">
                  <v-icon size="16" class="mr-1">mdi-twitter</v-icon>
                  Twitter: {{ count }}{{ languageStore.get('countUnit') }}
                </v-chip>
              </template>
            </div>
          </v-alert>

          <v-checkbox
              v-model="deleteSnsVideo"
              :label="languageStore.get('deleteSnsVideoTogether')"
              color="primary"
              hide-details
              density="compact"
          />
        </template>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-spacer />
        <v-btn color="error" variant="flat" @click="confirmDelete">
          {{ languageStore.get('delete') }}
        </v-btn>
        <v-btn variant="outlined" @click="close">
          {{ languageStore.get('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

:deep(.v-card-text.modal-body) {
  padding: 24px !important;
}

:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

.sns-info {
  margin-top: 8px;
}
</style>
